import React from 'react';
import { CardSectionRate } from '../../CardSectionRate/CardSectionRate';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { TextSummaryBox } from '@/components/TextSummaryBox/TextSummaryBox';
import { InspectionList } from '@/components/InspectionList';
import { InspectionItem } from '@/components/InspectionList/InspectionList.types';
import { RatingSection } from '@/api/mappers/mapper-types';
import { CardSectionRateWide, PhotoGallery, PhotoGalleryItem, SectionSummary, SVGIcon } from '@/components';
import { SectionNoData } from '@/components/SectionNoData';

interface RaportCarBottomProps {
  /** Sekcja oceny podwozia z tytułem, ratingiem i opisem */
  ratingSection: RatingSection;
  /** Lista uwag dotyczących kontroli podwozia */
  remarks: string[];
  /** Lista elementów podwozia do weryfikacji */
  chassisItems: InspectionItem[];
  /** Podsumowanie rzeczoznawcy dotyczące podwozia */
  expertSummary: string | null;
  galleryPhotos?: PhotoGalleryItem[];
}

export default function RaportCarBottom({
  ratingSection,
  remarks,
  chassisItems,
  expertSummary,
  galleryPhotos
}: RaportCarBottomProps) {
  return (
    <>
      <div className="mt-[58px] w-full" id="podsumowanie-kontroli-podwozia">
        <SectionSubtitle
          title="Podsumowanie kontroli podwozia"
          description="Ocena punktowa oraz zestawienie uwag."
        />

        <div className="flex flex-col gap-11 items-start mt-11 md:flex-row">
          {ratingSection.rating !== null ? (
            ratingSection.rating === 5 ?
              <CardSectionRateWide
                title={ratingSection.title}
                rating={ratingSection.rating}
                description={'Wygląda na to, że nasza ekspertyza nie wykazała żadnych nieprawidłowości w ocenie podwozia.'}
                ratingMessage={ratingSection.ratingMessage}
              />
              : <>
                <CardSectionRate
                  title={ratingSection.title}
                  rating={ratingSection.rating}
                  description={ratingSection.description ?? undefined}
                  ratingMessage={ratingSection.ratingMessage}
                />
                {remarks.length > 0 && <TextSummaryBox title="Na to zwróć uwagę: " items={remarks} />}
              </>
          ) :
            <SectionNoData
              title="Ocena podwozia w przygotowaniu"
              description="Rzeczoznawca pracuje nad oceną podwozia. Ocena będzie dostępna wkrótce."
            />}

        </div>
      </div>

      {chassisItems.length > 0 && (
        <div className="mt-20" id="stan-podwozia">
          <SectionSubtitle
            title="Stan podwozia"
            description="Weryfikacja podwozia – sprawdzenie stanu elementów układu jezdnego i napędowego."
          />

          <InspectionList
            className="mt-10"
            items={chassisItems}
            remarks={remarks}
          />
          {expertSummary && (
            <div className="mt-20 md:mt-0 md:px-8">
              <SectionSummary
                iconName="network-nodes"
                title=" Podsumowanie Rzeczoznawcy dotyczące stanu elementów podwozia"
                content={expertSummary}
                className="md:border-t-0 md:rounded-t-none"
              />
            </div>
          )}
        </div>
      )}

      {/* Galeria podwozia */}
      {galleryPhotos && galleryPhotos.length > 0 && (
        <div id="galeria-podwozia" className="mt-12">
          <PhotoGallery
            photos={galleryPhotos}
            title="Galeria podwozia pojazdu"
            iconName="network-nodes"
            collapsible={true}
            defaultExpanded={false}
            previewCount={4}
            galleryVariant="default"
          />
        </div>
      )}

      <p className="mt-10 text-sm text-left lg:pl-8 text-text-secondary">
        <strong>WAŻNE!</strong> Rzeczoznawca nie ingeruje w konstrukcję pojazdu i podczas oględzin nie zdejmuje plastikowych osłon. Oględziny podwozia, w tym stanu dolnej części silnika, wykonuje się bez demontażu jego osłony, jeśli taka występuje podczas kontroli. Ocena pod kątem ewentualnych wycieków odbywa się wyłącznie w zakresie widocznym wokół osłon oraz poprzez specjalne otwory kontrolne, z maksymalną możliwą starannością.
      </p>
    </>
  );
}
